import { Auth0Context, type Auth0ContextInterface } from '@auth0/auth0-react'
import React, { useMemo } from 'react'

import { DEV_BYPASS_USER } from '../devBypass'

const noopAsyncString = async (): Promise<string> => 'dev-bypass-token'
const noopAsyncVoid = async (): Promise<void> => undefined
const noopAsyncRedirect = async (): Promise<never> => {
  throw new Error('Auth flows are disabled while VITE_DEV_BYPASS_AUTH is active')
}
const notImplemented = () => {
  throw new Error('Auth0 method called under VITE_DEV_BYPASS_AUTH (no-op in local dev)')
}

/**
 * Drop-in replacement for `Auth0Provider` that exposes a stub authenticated
 * context to the rest of the app without instantiating the Auth0 SDK.
 *
 * Active only when {@link isDevAuthBypassActive} is true. See devBypass.ts.
 */
const DevAuthBypassProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useMemo<Auth0ContextInterface>(
    () =>
      ({
        isAuthenticated: true,
        isLoading: false,
        user: { ...DEV_BYPASS_USER },
        error: undefined,
        getAccessTokenSilently: noopAsyncString,
        getAccessTokenWithPopup: noopAsyncString,
        getIdTokenClaims: async () => undefined,
        loginWithCustomTokenExchange: noopAsyncRedirect,
        exchangeToken: noopAsyncRedirect,
        loginWithRedirect: noopAsyncVoid,
        loginWithPopup: noopAsyncVoid,
        connectAccountWithRedirect: noopAsyncVoid,
        logout: noopAsyncVoid,
        handleRedirectCallback: noopAsyncRedirect,
        getDpopNonce: notImplemented,
        setDpopNonce: notImplemented,
        generateDpopProof: notImplemented,
        createFetcher: notImplemented,
        getConfiguration: () => ({ domain: 'bypass.local', clientId: 'bypass' }),
        mfa: notImplemented as unknown as Auth0ContextInterface['mfa'],
      }) as unknown as Auth0ContextInterface,
    []
  )

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
}

export default DevAuthBypassProvider
